import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, User, Phone, Mail, Building2, DollarSign, Clock, AlertCircle } from 'lucide-react';
import { createResource } from '../lib/resources';
import { getResourceRoles, ResourceRole } from '../lib/resourceRoles';

interface ResourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  businessId?: string;
}

export default function ResourceModal({ isOpen, onClose, onSuccess, businessId }: ResourceModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [permissionRoles, setPermissionRoles] = useState<ResourceRole[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    role_id: '', // Permission role ID
    compensation_type: 'hourly' as 'hourly' | 'flat_rate' | 'commission',
    hourly_rate: '0',
    flat_rate_amount: '0',
    commission_percentage: '0',
    status: 'active',
    is_admin: false,
    password: '',
    confirm_password: '',
    notes: ''
  });

  const loadPermissionRoles = async () => {
    if (!businessId) return;
    
    try {
      const roles = await getResourceRoles(businessId);
      setPermissionRoles(roles);
    } catch (err) {
      console.error('Error loading permission roles:', err);
      setError(err instanceof Error ? err.message : 'Failed to load permission roles');
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadPermissionRoles();
    }
  }, [isOpen, businessId]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      // Reset form data when modal closes
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        role: '',
        role_id: '',
        compensation_type: 'hourly',
        hourly_rate: '0',
        flat_rate_amount: '0',
        commission_percentage: '0',
        status: 'active',
        is_admin: false,
        password: '',
        confirm_password: '',
        notes: ''
      });
      setError(null);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('🔍 [DEBUG] Starting team member creation process');
    console.log('🔍 [DEBUG] Business ID:', businessId);
    console.log('🔍 [DEBUG] Form data:', formData);
    
    if (!businessId) {
      console.error('❌ [DEBUG] No business selected');
      setError('No business selected');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      console.log('🔍 [DEBUG] Starting validation...');

      // Basic validation
      if (!formData.first_name.trim()) {
        console.error('❌ [DEBUG] First name validation failed');
        throw new Error('First name is required');
      }
      if (!formData.last_name.trim()) {
        console.error('❌ [DEBUG] Last name validation failed');
        throw new Error('Last name is required');
      }
      if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
        console.error('❌ [DEBUG] Email validation failed:', formData.email);
        throw new Error('Valid email is required');
      }

      if (!formData.password) {
        console.error('❌ [DEBUG] Password validation failed');
        throw new Error('Password is required');
      }

      // Validate password
      if (formData.password.length < 6) {
        console.error('❌ [DEBUG] Password length validation failed');
        throw new Error('Password must be at least 6 characters');
      }
      if (formData.password !== formData.confirm_password) {
        console.error('❌ [DEBUG] Password confirmation validation failed');
        throw new Error('Passwords do not match');
      }

      console.log('✅ [DEBUG] All validations passed');

      // Initialize compensation values
      const compensationData = {
        hourly_rate: 0,
        flat_rate_amount: 0,
        commission_percentage: 0,
        commission_rate: 0
      };

      // Set compensation based on type
      if (formData.compensation_type === 'hourly') {
        compensationData.hourly_rate = parseFloat(formData.hourly_rate) || 0;
        console.log('🔍 [DEBUG] Setting hourly rate:', compensationData.hourly_rate);
      } else if (formData.compensation_type === 'flat_rate') {
        compensationData.flat_rate_amount = parseFloat(formData.flat_rate_amount) || 0;
        console.log('🔍 [DEBUG] Setting flat rate amount:', compensationData.flat_rate_amount);
      } else if (formData.compensation_type === 'commission') {
        compensationData.commission_percentage = parseFloat(formData.commission_percentage) || 0;
        compensationData.commission_rate = compensationData.commission_percentage / 100;
        console.log('🔍 [DEBUG] Setting commission percentage:', compensationData.commission_percentage);
      }

      console.log('🔍 [DEBUG] Compensation data:', compensationData);

      // Create the resource payload
      const resourcePayload = {
        business_id: businessId,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        role: 'DEFAULT', // Default value for legacy role column
        role_id: formData.role_id || undefined,
        status: formData.status,
        compensation_type: formData.compensation_type,
        is_admin: formData.is_admin,
        password: formData.password,
        confirm_password: formData.confirm_password,
        earnings_ytd: 0,
        earnings_mtd: 0,
        earnings_last_month: 0,
        is_mobile_available: false,
        schedule_availability: {
          monday: { enabled: false, shifts: [] },
          tuesday: { enabled: false, shifts: [] },
          wednesday: { enabled: false, shifts: [] },
          thursday: { enabled: false, shifts: [] },
          friday: { enabled: false, shifts: [] },
          saturday: { enabled: false, shifts: [] },
          sunday: { enabled: false, shifts: [] }
        },
        ...compensationData
      };

      console.log('🔍 [DEBUG] Resource payload created:', resourcePayload);
      console.log('🔍 [DEBUG] About to call createResource...');

      const result = await createResource(resourcePayload);
      console.log('✅ [DEBUG] createResource completed successfully:', result);
      
      onSuccess();
      console.log('✅ [DEBUG] onSuccess callback called');
    } catch (err) {
      console.error('❌ [DEBUG] Error creating resource:', err);
      console.error('❌ [DEBUG] Error details:', {
        message: err instanceof Error ? err.message : 'Unknown error',
        stack: err instanceof Error ? err.stack : undefined,
        name: err instanceof Error ? err.name : undefined
      });
      setError(err instanceof Error ? err.message : 'Failed to create team member');
    } finally {
      console.log('🔍 [DEBUG] Setting loading to false');
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const modalContent = (
    <div 
      className={`fixed inset-0 z-[9999] transition-opacity duration-300 ${
        isOpen ? 'opacity-100' : 'opacity-0'
      }`}
      onTransitionEnd={handleTransitionEnd}
    >
      <div 
        className={`fixed inset-0 bg-gradient-to-br from-purple-50 via-white to-purple-50 transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          touchAction: 'manipulation',
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
        }}
      >
        <div className="h-screen flex flex-col">
          {/* Header with Mobile-Optimized Design */}
          <div className="relative bg-gray-800 border-b border-gray-700 shadow-lg">
            <div className="flex items-center justify-between px-2 py-2 sm:px-4 sm:py-4">
              <div className="flex items-center space-x-2 flex-1 min-w-0">
                <button 
                  onClick={handleClose} 
                  className="flex items-center justify-center w-8 h-8 sm:w-11 sm:h-11 rounded-lg sm:rounded-full bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 touch-manipulation shadow-sm"
                  style={{ minWidth: '32px', minHeight: '32px' }}
                >
                  <X className="h-4 w-4 sm:h-6 sm:w-6" />
                </button>
                <div className="flex-1 min-w-0">
                  <h2 className="text-base sm:text-lg font-bold text-white truncate">
                    New Team Member
                  </h2>
                  <div className="flex items-center mt-0.5">
                    <span className="text-xs sm:text-sm text-gray-300 truncate">
                      Add a new team member to your business
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content with mobile-optimized styling */}
          <div className="flex-1 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
            <div className="max-w-2xl mx-auto py-1 px-2 sm:py-6 sm:px-6">
              {error && (
                <div className="mb-3 sm:mb-6 backdrop-blur-xl bg-red-50/90 border border-red-200/50 text-red-700 px-3 py-2 sm:px-4 sm:py-3 rounded-xl sm:rounded-2xl text-xs sm:text-sm shadow-sm"
                     style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                  <div className="flex items-center">
                    <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                    {error}
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-1.5 sm:space-y-6">
                {/* Basic Information Section */}
                <div className="backdrop-blur-xl bg-white/70 rounded-2xl border border-purple-200/30 overflow-hidden shadow-lg"
                     style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
                  <div className="px-2 py-1.5 sm:px-6 sm:py-4 border-b border-purple-200/30 bg-purple-50/30">
                    <h3 className="text-xs sm:text-base font-bold text-gray-900">Basic Information</h3>
                  </div>
                  <div className="p-2 sm:p-6">
                    <div className="space-y-4 sm:space-y-6">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                            First Name
                          </label>
                          <input
                            type="text"
                            required
                            value={formData.first_name}
                            onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                            className="w-full px-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                            style={{ 
                              minHeight: '44px',
                              fontSize: '16px',
                              backdropFilter: 'blur(10px)',
                              WebkitBackdropFilter: 'blur(10px)'
                            }}
                            placeholder="Enter first name"
                          />
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                            Last Name
                          </label>
                          <input
                            type="text"
                            required
                            value={formData.last_name}
                            onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                            className="w-full px-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                            style={{ 
                              minHeight: '44px',
                              fontSize: '16px',
                              backdropFilter: 'blur(10px)',
                              WebkitBackdropFilter: 'blur(10px)'
                            }}
                            placeholder="Enter last name"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                          Email Address
                        </label>
                        <input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                          className="w-full px-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                          style={{ 
                            minHeight: '44px',
                            fontSize: '16px',
                            backdropFilter: 'blur(10px)',
                            WebkitBackdropFilter: 'blur(10px)'
                          }}
                          placeholder="Enter email address"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                          className="w-full px-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                          style={{ 
                            minHeight: '44px',
                            fontSize: '16px',
                            backdropFilter: 'blur(10px)',
                            WebkitBackdropFilter: 'blur(10px)'
                          }}
                          placeholder="Enter phone number"
                        />
                      </div>



                      <div>
                        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                          Permission Role
                        </label>
                        <select
                          value={formData.role_id}
                          onChange={(e) => setFormData(prev => ({ ...prev, role_id: e.target.value }))}
                          className="w-full px-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                          style={{ 
                            minHeight: '44px',
                            fontSize: '16px',
                            backdropFilter: 'blur(10px)',
                            WebkitBackdropFilter: 'blur(10px)'
                          }}
                        >
                          <option value="">No specific permissions (optional)</option>
                          {permissionRoles.map(role => (
                            <option key={role.id} value={role.id}>
                              {role.name}
                            </option>
                          ))}
                        </select>
                        <p className="mt-1 text-xs sm:text-sm text-gray-600 font-medium">
                          Assign system access permissions to this team member
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Compensation Section */}
                <div className="backdrop-blur-xl bg-white/70 rounded-2xl border border-purple-200/30 overflow-hidden shadow-lg"
                     style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
                  <div className="px-2 py-1.5 sm:px-6 sm:py-4 border-b border-purple-200/30 bg-purple-50/30">
                    <h3 className="text-xs sm:text-base font-bold text-gray-900">Compensation</h3>
                  </div>
                  <div className="p-2 sm:p-6">
                    <div className="space-y-4 sm:space-y-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                          Compensation Type
                        </label>
                        <select
                          value={formData.compensation_type}
                          onChange={(e) => setFormData(prev => ({ ...prev, compensation_type: e.target.value as 'hourly' | 'flat_rate' | 'commission' }))}
                          className="w-full px-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                          style={{ 
                            minHeight: '44px',
                            fontSize: '16px',
                            backdropFilter: 'blur(10px)',
                            WebkitBackdropFilter: 'blur(10px)'
                          }}
                        >
                          <option value="hourly">Hourly Rate</option>
                          <option value="flat_rate">Flat Rate</option>
                          <option value="commission">Commission</option>
                        </select>
                      </div>
                      
                      {formData.compensation_type === 'hourly' && (
                        <div>
                          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                            Hourly Rate
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                              <span className="text-gray-500 text-base font-medium">$</span>
                            </div>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={formData.hourly_rate}
                              onChange={(e) => setFormData(prev => ({ ...prev, hourly_rate: e.target.value }))}
                              className="w-full pl-8 pr-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                              style={{ 
                                minHeight: '44px',
                                fontSize: '16px',
                                backdropFilter: 'blur(10px)',
                                WebkitBackdropFilter: 'blur(10px)'
                              }}
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                      )}
                      
                      {formData.compensation_type === 'flat_rate' && (
                        <div>
                          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                            Flat Rate Amount
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                              <span className="text-gray-500 text-base font-medium">$</span>
                            </div>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={formData.flat_rate_amount}
                              onChange={(e) => setFormData(prev => ({ ...prev, flat_rate_amount: e.target.value }))}
                              className="w-full pl-8 pr-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                              style={{ 
                                minHeight: '44px',
                                fontSize: '16px',
                                backdropFilter: 'blur(10px)',
                                WebkitBackdropFilter: 'blur(10px)'
                              }}
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                      )}
                      
                      {formData.compensation_type === 'commission' && (
                        <div>
                          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                            Commission Rate
                          </label>
                          <div className="relative">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              max="100"
                              value={formData.commission_percentage}
                              onChange={(e) => setFormData(prev => ({ ...prev, commission_percentage: e.target.value }))}
                              className="w-full pl-4 pr-8 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                              style={{ 
                                minHeight: '44px',
                                fontSize: '16px',
                                backdropFilter: 'blur(10px)',
                                WebkitBackdropFilter: 'blur(10px)'
                              }}
                              placeholder="0"
                            />
                            <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                              <span className="text-gray-500 text-base font-medium">%</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Login Credentials Section */}
                <div className="backdrop-blur-xl bg-white/70 rounded-2xl border border-purple-200/30 overflow-hidden shadow-lg"
                     style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
                  <div className="px-2 py-1.5 sm:px-6 sm:py-4 border-b border-purple-200/30 bg-purple-50/30">
                    <h3 className="text-xs sm:text-base font-bold text-gray-900">Login Credentials</h3>
                  </div>
                  <div className="p-2 sm:p-6">
                    <div className="space-y-4 sm:space-y-6">
                      <div>
                        <label className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={formData.is_admin}
                            onChange={(e) => setFormData(prev => ({ ...prev, is_admin: e.target.checked }))}
                            className="h-5 w-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500 focus:ring-2 transition-all duration-200"
                          />
                          <span className="text-sm sm:text-base text-gray-700 font-semibold">Admin Access</span>
                        </label>
                        <p className="mt-2 text-xs sm:text-sm text-gray-600 font-medium ml-8">
                          Grant administrative privileges to this team member
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                          Password
                        </label>
                        <input
                          type="password"
                          required
                          value={formData.password}
                          onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                          className="w-full px-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                          style={{ 
                            minHeight: '44px',
                            fontSize: '16px',
                            backdropFilter: 'blur(10px)',
                            WebkitBackdropFilter: 'blur(10px)'
                          }}
                          placeholder="Enter password"
                        />
                        <p className="mt-1 text-xs sm:text-sm text-gray-600 font-medium">
                          Required for team members who need system access
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                          Confirm Password
                        </label>
                        <input
                          type="password"
                          required
                          value={formData.confirm_password}
                          onChange={(e) => setFormData(prev => ({ ...prev, confirm_password: e.target.value }))}
                          className="w-full px-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                          style={{ 
                            minHeight: '44px',
                            fontSize: '16px',
                            backdropFilter: 'blur(10px)',
                            WebkitBackdropFilter: 'blur(10px)'
                          }}
                          placeholder="Confirm password"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Notes Section */}
                <div className="backdrop-blur-xl bg-white/70 rounded-2xl border border-purple-200/30 overflow-hidden shadow-lg"
                     style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
                  <div className="px-2 py-1.5 sm:px-6 sm:py-4 border-b border-purple-200/30 bg-purple-50/30">
                    <h3 className="text-xs sm:text-base font-bold text-gray-900">Notes</h3>
                  </div>
                  <div className="p-2 sm:p-6">
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                      rows={4}
                      className="w-full px-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation resize-none"
                      style={{ 
                        minHeight: '100px',
                        fontSize: '16px',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)'
                      }}
                      placeholder="Add any additional notes about this team member..."
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-2 sm:pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full inline-flex items-center justify-center px-6 py-4 text-base font-bold text-white bg-purple-600 rounded-2xl hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-lg touch-manipulation"
                    style={{ minHeight: '56px' }}
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                        Creating Team Member...
                      </>
                    ) : (
                      'Create Team Member'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // 🚀 FIX: Use React Portal to render modal outside parent DOM tree  
  // This prevents PWA stacking context issues and ensures modal appears above navigation
  return createPortal(modalContent, document.body);
}